import { useMemo, useEffect, useRef } from 'preact/hooks'
import { ExternalLink, RefreshCw } from 'lucide-preact'
import type { JSX } from 'preact'
import type { FileImportRowGridDto } from '@/types/campaignAnalytics'
import { PhoneEnrichmentCell } from '@/components/common/PhoneEnrichmentCell'
import { EnrichmentStatusCell } from '@/components/common/EnrichmentStatusCell'
import { PersonAvatar } from '@/components/common/Avatar'
import { DataGrid, type ColumnConfig } from '@/components/common/grids/DataGrid'
import { useUserProfile } from '@/hooks/useUserProfile'
import { useCompany } from '@/hooks/useCompany'
import { AUDIENCE_CONTACT_EXPORT_COLUMNS, generateExportFilename } from '@/utils/leadExport'

interface CampaignListContactsSectionProps {
  filtered: FileImportRowGridDto[]
  search: string
  setSearch: (val: string) => void
  country: string
  setCountry: (val: string) => void
  countryOptions: string[]
  clientCompanyId: string
  workspaceId: string
  campaignId: string
  onSelectContact?: (c: FileImportRowGridDto) => void
  onContactUpdated?: (updated: Partial<FileImportRowGridDto> & { Id: string }) => void
  onRefresh?: () => void | Promise<void>
  refreshing?: boolean
}

export const CampaignListContactsSection = ({
  filtered,
  search,
  setSearch,
  country,
  setCountry,
  countryOptions,
  clientCompanyId,
  workspaceId,
  campaignId,
  onSelectContact,
  onContactUpdated,
  onRefresh,
  refreshing,
}: CampaignListContactsSectionProps) => {
  const { getWorkspaceCapabilities } = useUserProfile()
  const { canEdit } = getWorkspaceCapabilities(clientCompanyId, workspaceId)
  const { company, currentWorkspace } = useCompany()
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map())

  // Cleanup: abort all in-flight requests on unmount
  useEffect(() => {
    return () => {
      abortControllersRef.current.forEach(controller => controller.abort())
      abortControllersRef.current.clear()
    }
  }, [])

  // Define columns for DataGrid
  const columns: ColumnConfig<FileImportRowGridDto>[] = useMemo(() => [
    {
      key: 'Name',
      header: 'Name',
      sortable: true,
      accessor: (contact) => [contact.FirstName, contact.LastName].filter(Boolean).join(' ').toLowerCase(),
      render: (contact) => (
        <div className="flex items-center gap-3">
          <PersonAvatar
            firstName={contact.FirstName}
            lastName={contact.LastName}
            profilePicUrl={contact.ProfilePicUrl}
            size="md"
          />
          <div className="flex flex-col">
            <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
              {[contact.FirstName, contact.LastName].filter(Boolean).join(' ') || '—'}
            </div>
            {contact.Email && <div className="text-xs text-slate-500 dark:text-slate-400">{contact.Email}</div>}
          </div>
        </div>
      ),
    },
    {
      key: 'Phone',
      header: 'Phone',
      sortable: true,
      accessor: (contact) => (contact.Phone || '').toLowerCase(),
      render: (contact) => (
        <PhoneEnrichmentCell
          key={`phone-${contact.Id}-${contact.Phone}-${contact.GlobalContactPhone}`}
          phone={contact.Phone}
          globalContactPhone={contact.GlobalContactPhone}
          phoneEnrichmentAvailableDate={contact.PhoneEnrichmentAvailableDate}
          id={contact.Id}
          clientCompanyId={clientCompanyId}
          workspaceId={workspaceId}
          campaignId={campaignId}
          canEdit={canEdit}
          context="fileImportRow"
          isLastRow={false}
          onPhoneUpdated={(updated) => {
            const finalUpdate = {
              Id: contact.Id,
              ...updated,
              Phone: updated.GlobalContactPhone || updated.Phone
            }
            onContactUpdated?.(finalUpdate)
          }}
        />
      ),
    },
    {
      key: 'Enrichment',
      header: 'Enrichment',
      sortable: false,
      hidden: 'hidden sm:table-cell',
      render: (contact) => (
        <EnrichmentStatusCell
          enrichmentStatus={contact.EnrichmentStatus}
          enrichedAt={contact.EnrichedAt}
          enrichmentAvailableDate={contact.EnrichmentAvailableDate}
          enrichmentAttemptCount={contact.EnrichmentAttemptCount || 0}
          clientCompanyId={clientCompanyId}
          workspaceId={workspaceId}
          campaignId={campaignId}
          id={contact.Id}
          context="fileImportRow"
          onEnrichmentUpdated={(updated) => {
            onContactUpdated?.({
              Id: contact.Id,
              ...updated
            })
          }}
        />
      ),
    },
    {
      key: 'Title',
      header: 'Title',
      sortable: true,
      accessor: (contact) => (contact.JobTitle || '').toLowerCase(),
      render: (contact) => contact.JobTitle || '—',
    },
    {
      key: 'Company',
      header: 'Company',
      sortable: true,
      accessor: (contact) => (contact.CompanyName || '').toLowerCase(),
      render: (contact) => {
        if (!contact.CompanyName) return '—'
        if (contact.CompanyLinkedInId) {
          return (
            <a
              href={`https://www.linkedin.com/company/${contact.CompanyLinkedInId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
              onClick={e => e.stopPropagation()}
            >
              {contact.CompanyName}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )
        }
        return contact.CompanyName
      },
    },
    {
      key: 'Country',
      header: 'Country',
      sortable: true,
      hidden: 'hidden md:table-cell',
      accessor: (contact) => (contact.Country || '').toLowerCase(),
      render: (contact) => contact.Country || '—',
    },
    {
      key: 'LinkedIn',
      header: 'LinkedIn',
      sortable: false,
      render: (contact) => {
        if (!contact.LinkedinProfileUrl) return '—'
        return (
          <a
            href={contact.LinkedinProfileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
            onClick={e => e.stopPropagation()}
          >
            View <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )
      },
    },
  ], [clientCompanyId, workspaceId, campaignId, canEdit, onContactUpdated])

  // Every sortable column here carries an accessor, so the grid builds its own comparator. This file
  // used to hand-roll one that did nothing but read those same accessors (ORB-715).

  const exportConfig = useMemo(() => ({
    getFilename: () => generateExportFilename(company?.Name, currentWorkspace?.Name, 'Audience', 'contacts'),
    columns: AUDIENCE_CONTACT_EXPORT_COLUMNS.map(col => ({ header: col.label, getValue: col.getValue })),
  }), [company?.Name, currentWorkspace?.Name])

  return (
    <div className="relative">
      {refreshing && (
        <div className="absolute inset-0 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-slate-600 dark:text-slate-300">Refreshing...</span>
          </div>
        </div>
      )}

      {/* The Filters card and the grid's own heading strip are gone: search and country are toolbar
          controls now, and the card they sat in was a second bordered box around DataGrid's own. */}
      <DataGrid
        title="Contacts"
        data={filtered}
        columns={columns}
        getRowKey={(contact) => contact.Id}
        onRowClick={(contact) => onSelectContact?.(contact)}
        defaultSortKey="Name"
        pagination
        // Search and country both rebuild the set, and two different filters can leave the same number
        // of rows — so row count alone cannot tell the grid to go back to page one.
        pageResetKey={`${search}|${country}`}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: 'Search contacts',
          ariaLabel: 'Search contacts by name, email or company',
        }}
        toolbarActions={
          <>
            <select
              value={country}
              onChange={(e: JSX.TargetedEvent<HTMLSelectElement, Event>) => setCountry(e.currentTarget.value)}
              aria-label="Filter by country"
              className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 py-1.5 text-xs text-slate-700 dark:text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <option value="">All countries</option>
              {countryOptions.map((c: string) => <option key={c} value={c}>{c}</option>)}
            </select>
            {onRefresh && (
              <button
                onClick={() => onRefresh()}
                disabled={refreshing}
                className="inline-flex items-center gap-1.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2.5 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                title="Refresh from server"
              >
                <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                Refresh
              </button>
            )}
          </>
        }
        exportConfig={exportConfig}
        emptyMessage="No contacts found"
      />
    </div>
  )
}
